import { Operation, Task } from 'src/common/interfaces/activity-model';
import { estimateTypes } from 'src/enums/tasks';

function meanPERT(min: number, mode: number, max: number): number {
  return (min + 4 * mode + max) / 6;
}

function meanTriangular(min: number, mode: number, max: number): number {
  return (min + mode + max) / 3;
}

function meanUniform(min: number, max: number): number {
  return (min + max) / 2;
}

function meanSpike(mode: number): number {
  return mode;
}

// 8-point Gauss-Legendre nodes and weights on [-1, 1].
const GAUSS_NODES = [
  -0.960289856497536, -0.796666477413627, -0.525532409916329, -0.18343464249565, 0.18343464249565, 0.525532409916329,
  0.796666477413627, 0.960289856497536,
];
const GAUSS_WEIGHTS = [
  0.101228536290376, 0.222381034453374, 0.313706645877887, 0.362683783378362, 0.362683783378362, 0.313706645877887,
  0.222381034453374, 0.101228536290376,
];
function meanPERTInverse(min: number, mode: number, max: number): number {
  // Analytical solution for mean of 1 / PERT does not exist, so we use numerical integration.
  // This is 8-point Gauss-Legendre quadrature on the log scale.
  if (max === min) return 1 / min; // Becomes spike
  const alpha = 1 + (4 * (mode - min)) / (max - min);
  const beta = 1 + (4 * (max - mode)) / (max - min);
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const centre = (logMin + logMax) / 2;
  const halfWidth = (logMax - logMin) / 2;
  let integral = 0;
  let normalisation = 0;
  for (let index = 0; index < GAUSS_NODES.length; index++) {
    const speed = Math.exp(centre + halfWidth * GAUSS_NODES[index]);
    const x = Math.min(Math.max((speed - min) / (max - min), 0), 1);
    const density = Math.pow(x, alpha - 1) * Math.pow(1 - x, beta - 1) * GAUSS_WEIGHTS[index];
    if (!Number.isFinite(density)) continue;
    integral += density;
    normalisation += density * speed;
  }
  return normalisation > 0 ? integral / normalisation : 0;
}

function meanUniformInverse(min: number, max: number): number {
  // Mean of 1 / UNIFORM has analyical solution ln(b/a)/(b−a)
  if (max === min) return 1 / min; //Becomes spike
  return Math.log(max / min) / (max - min);
}

function meanSpikeInverse(mode: number): number {
  if (mode === 0) return 0;
  return 1 / mode;
}

function meanTaskOpTime(task: Task, overrideDist?: string): number {
  let dist = overrideDist ?? task.distribution;
  let meanTime = 0;
  switch (dist) {
    case 'pert':
      meanTime = meanPERT(task.min, task.most, task.max);
      break;
    case 'triangular':
      meanTime = meanTriangular(task.min, task.most, task.max);
      break;
    case 'uniform':
      meanTime = meanUniform(task.min, task.max);
      break;
    case 'spike':
      meanTime = meanSpike(task.most);
      break;
    default:
    // No action if no recognised distribution. Maybe add an error here?
  }
  return meanTime;
}

function meanTaskOpSpeed(task: Task, overrideDist?: string): number {
  let dist = overrideDist ?? task.distribution;
  let meanTime = 0;
  let distance = Math.abs(Number(task.to) - Number(task.from));
  switch (dist) {
    case 'pert':
      meanTime = meanPERTInverse(task.min, task.most, task.max) * distance;
      break;
    case 'uniform':
      meanTime = meanUniformInverse(task.min, task.max) * distance;
      break;
    case 'spike':
      meanTime = meanSpikeInverse(task.most) * distance;
      break;
    default:
    // No action if no recognised distribution. Maybe add an error here?
  }
  return meanTime;
}

function meanTask(task: Task, overrideDist?: string, overrideEstimateType?: number): number {
  let dist = overrideDist ?? task.distribution;
  let estimateType = overrideEstimateType ?? task.estimateType;
  let meanTime = 0;
  if (estimateType === estimateTypes.OPERATION_SPEED) {
    meanTime = meanTaskOpSpeed(task, dist);
  } else {
    // Default to operation time if not speed
    meanTime = meanTaskOpTime(task, dist);
  }
  return meanTime;
}

function cumulativeCertainty(task: Task, taskList: Task[]): number {
  let certainty = task.certainty / 100;
  while (task.parentId !== null) {
    task = taskList.find((t) => t.taskId === task.parentId)!;
    certainty *= task.certainty / 100;
  }
  return certainty;
}

export function meanOperation(operation: Operation): number {
  return meanTaskList(operation.tasks);
}

export function meanTaskList(taskList: Task[]): number {
  let sum = 0;
  let meanTime = 0;

  for (let index = 0; index < taskList.length; index++) {
    let task = taskList[index];

    // Calculate contribution from this task
    if (task.isBranch) {
      let parent = taskList.find((t) => t.taskId === task.parentId);
      meanTime = meanTask(task, parent?.distribution, parent?.estimateType);
    } else {
      meanTime = task.withBranch ? 0 : meanTask(task);
    }
    let certainty = cumulativeCertainty(task, taskList);
    sum += meanTime * certainty;
  }
  return sum;
}

export function meanAllActivities(operations: Operation[]): number[] {
  const result: number[] = [];
  for (let index = 0; index < operations.length; index++) {
    result.push(meanOperation(operations[index]));
  }
  return result;
}
